import { colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';
import labPlugin from 'colord/plugins/lab';
extend([
    a11yPlugin,
    labPlugin
]);
export const WCAG_AA_MIN = 4.5;
export const DARK_900 = '#282863';
export const DARK_700 = '#696992';
export const DARK_50 = '#fcfcfd';
export const DARK_200 = '#d4d4e0';
const DARK_300 = '#bfbfd0';
const DARK_600 = '#7e7ea1';
export function getContrastRatio(bgHex, textHex) {
    const bg = colord(bgHex);
    const text = colord(textHex);
    if (!bg.isValid() || !text.isValid()) return 1;
    return bg.contrast(text);
}
export function getTextColorModeForBackground(bgHex) {
    const c = colord(bgHex);
    if (!c.isValid()) return 'dark';
    const contrastLight = c.contrast(DARK_50);
    const contrastDark = c.contrast(DARK_900);
    if (contrastDark >= WCAG_AA_MIN) return 'dark';
    if (contrastLight >= WCAG_AA_MIN) return 'light';
    return 'dark';
}
export function getTextColorsForBackground(bgHex) {
    const mode = getTextColorModeForBackground(bgHex);
    return mode === 'dark' ? {
        primary: DARK_900,
        secondary: DARK_700
    } : {
        primary: DARK_50,
        secondary: DARK_200
    };
}
export function getDisabledTextColorForBackground(bgHex) {
    const c = colord(bgHex);
    if (!c.isValid()) return DARK_600;
    const contrast600 = getContrastRatio(bgHex, DARK_600);
    const contrast300 = getContrastRatio(bgHex, DARK_300);
    return contrast300 >= contrast600 ? DARK_300 : DARK_600;
}
const LIGHTNESS_MIN = 0.18;
const LIGHTNESS_MAX = 0.92;
export function adjustBackgroundForContrast(bgHex) {
    let c = colord(bgHex);
    if (!c.isValid()) return bgHex;
    const step = 0.06;
    const maxIterations = 15;
    for(let i = 0; i < maxIterations; i++){
        const hex = c.toHex();
        if (getContrastRatio(hex, DARK_900) >= WCAG_AA_MIN) return hex;
        if (getContrastRatio(hex, DARK_50) >= WCAG_AA_MIN) return hex;
        const hsl = c.toHsl();
        hsl.s = Math.min(0.8, hsl.s);
        hsl.l = hsl.l > 0.5 ? Math.min(LIGHTNESS_MAX, hsl.l + step) : Math.max(LIGHTNESS_MIN, hsl.l - step);
        c = colord({
            h: hsl.h,
            s: hsl.s,
            l: hsl.l,
            a: hsl.a
        });
    }
    return c.toHex();
}
const GRADIENT_DELTA_L_MIN = 8;
export function validateAndCorrectGradient(startHex, endHex) {
    let start = colord(startHex).isValid() ? startHex : DARK_900;
    let end = colord(endHex).isValid() ? endHex : DARK_900;
    const checkStops = (s, e)=>{
        const startDark = getContrastRatio(s, DARK_900) >= WCAG_AA_MIN;
        const startLight = getContrastRatio(s, DARK_50) >= WCAG_AA_MIN;
        const endDark = getContrastRatio(e, DARK_900) >= WCAG_AA_MIN;
        const endLight = getContrastRatio(e, DARK_50) >= WCAG_AA_MIN;
        if (startDark && endDark) return {
            useDark: true,
            needsCorrection: false
        };
        if (startLight && endLight) return {
            useDark: false,
            needsCorrection: false
        };
        return {
            useDark: true,
            needsCorrection: true
        };
    };
    let result = checkStops(start, end);
    if (result.needsCorrection) {
        start = adjustBackgroundForContrast(start);
        end = adjustBackgroundForContrast(end);
        result = checkStops(start, end);
    }
    const startLab = colord(start).toLab();
    const endLab = colord(end).toLab();
    const deltaL = Math.abs(startLab.l - endLab.l);
    if (deltaL < GRADIENT_DELTA_L_MIN) {
        var _endLab_alpha;
        const targetDelta = GRADIENT_DELTA_L_MIN - deltaL;
        const adjustEnd = endLab.l > startLab.l;
        const newEndL = adjustEnd ? Math.min(92, endLab.l + targetDelta) : Math.max(8, endLab.l - targetDelta);
        const labColor = {
            l: newEndL,
            a: endLab.a,
            b: endLab.b,
            alpha: (_endLab_alpha = endLab.alpha) !== null && _endLab_alpha !== void 0 ? _endLab_alpha : 1
        };
        end = colord(labColor).toHex();
    }
    const textPrimary = result.useDark ? DARK_900 : DARK_50;
    const textSecondary = result.useDark ? DARK_700 : DARK_200;
    return {
        startHex: start,
        endHex: end,
        textPrimary,
        textSecondary
    };
}
